import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';

import { trackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

export const EVENT_IDS = {
  APPLICATIONS_CLICK: '4313',
  APPLICATIONS_PANEL_OPEN: '4309',
  OFF_MARKET_LISTING_VIEW: '3441',
  ACTIVE_LISTING_VIEW: '3442',
  APPLICATIONS_ENABLE_FROM_NULL: '3405',
  APPLICATIONS_ENABLE_FROM_DISABLED: '3406',
  APPLICATIONS_DISABLE_FROM_ENABLED: '3407',
  APPLICATIONS_DISABLE_FROM_NULL: '3408',
  START_DEACTIVATION: '3721',
  PREVIEW_LISTING: '4406',
  JUMP_TO_ERROR: '9421',
} as const;

type ReviewPageEventProps = {
  currentProperty?: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
};

// https://dataportal.zillowgroup.net/event-registry/4313
export const emitApplicationsClickEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '265',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.APPLICATIONS_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review|active_listing',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'applications_view',
    },
    semantic: {
      semantic_event_nm: 'view_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4309
export const emitApplicationsPanelOpenEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '265',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.APPLICATIONS_PANEL_OPEN,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review|active_listing',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'applications_accordion',
    },
    semantic: {
      semantic_event_nm: 'start_applications_enablement',
      topic_tag_txt: [''],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3441
export const emitOffMarketListingViewEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.OFF_MARKET_LISTING_VIEW,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review|off_market_listing',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'posting_path_review|off_market_listing',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3442
export const emitActiveListingViewEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ACTIVE_LISTING_VIEW,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review|active_listing',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'posting_path_review|active_listing',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3405
export const emitApplicationsEnableFromNullEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.APPLICATIONS_ENABLE_FROM_NULL,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_null',
      trigger_source_nm: 'yes_most_common',
    },
    semantic: {
      semantic_event_nm: 'enable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3406
export const emitApplicationsEnableFromDisabledEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.APPLICATIONS_ENABLE_FROM_DISABLED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_disabled',
      trigger_source_nm: 'yes_most_common',
    },
    semantic: {
      semantic_event_nm: 'enable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3407
export const emitApplicationsDisableFromEnabledEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.APPLICATIONS_DISABLE_FROM_ENABLED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_enabled',
      trigger_source_nm: 'no',
    },
    semantic: {
      semantic_event_nm: 'disable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3408
export const emitApplicationsDisableFromNullEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.APPLICATIONS_DISABLE_FROM_NULL,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'applications_module_null',
      trigger_source_nm: 'no',
    },
    semantic: {
      semantic_event_nm: 'disable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3721
export const emitStartDeactivationEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_DEACTIVATION,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path_review|active_listing',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'deactivate_listing',
    },
    semantic: {
      semantic_event_nm: 'start_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4406
export const emitPreviewListingEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PREVIEW_LISTING,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'preview_listing',
    },
    semantic: {
      semantic_event_nm: 'preview_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9421
export const emitJumpToErrorInteractionEvent = ({ currentProperty, user }: ReviewPageEventProps) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.JUMP_TO_ERROR,
      event_type_version_id: '1',
      event_template_id: '265',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'properties',
      trigger_source_nm: 'jump_to_error',
      trigger_object_nm: 'review_page_footer',
    },
    semantic: {
      semantic_event_nm: 'errors_on_review_page',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
